import { useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DownloadIcon from '@mui/icons-material/Download'
import ShareIcon from '@mui/icons-material/Share'
import FilterPill from './FilterPill'

type ResultsHeaderProps = {
  title: string
  n: number
  onShare?: () => void
}

/** Study results title block: eyebrow, title + N pill, meta row, export buttons,
 *  and a filter pill bar. Export/filter actions are no-op / local UI. */
export default function ResultsHeader({ title, n, onShare }: ResultsHeaderProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')
  const [filters, setFilters] = useState({ age: true, duration: true })

  const exportBtnSx = {
    minWidth: 0,
    px: 1.5,
    py: 0.75,
    borderRadius: `${theme.affexy.radii.pill}px`,
    fontSize: 12.5,
    fontWeight: 600,
    textTransform: 'none' as const,
    bgcolor: c.gray50,
    color: c.ink,
    border: `1px solid ${c.ink08}`,
    '&:hover': { bgcolor: c.gray100 },
  }

  return (
    <Box
      sx={{
        bgcolor: c.white,
        border: `1px solid ${c.ink08}`,
        borderRadius: '18px',
        p: 2.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.25, flexWrap: 'wrap' }}>
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 280 }}>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: c.coral,
              fontWeight: 700,
            }}
          >
            {t('header.eyebrow')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h1" sx={{ fontSize: 26, m: 0, color: c.ink }}>
              {title}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.25,
                py: 0.375,
                borderRadius: `${theme.affexy.radii.pill}px`,
                bgcolor: alpha(c.coral, 0.1),
                color: c.coralHover,
                fontSize: 11.5,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {t('header.n', { n })}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', fontSize: 13, color: c.ink56 }}>
            <span>{t('header.dataRange')}</span>
            <Box component="span" sx={{ color: c.ink32 }}>·</Box>
            <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
              <Box
                component="span"
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c.success, animation: 'affexy-pulse 1.8s ease-in-out infinite' }}
              />
              {t('header.realtime')}
            </Box>
            <Box component="span" sx={{ color: c.ink32 }}>·</Box>
            <span>{t('header.lastUpdate')}</span>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button sx={exportBtnSx} startIcon={<SlideshowIcon sx={{ fontSize: 15 }} />}>
            {t('header.powerpoint')}
          </Button>
          <Button sx={exportBtnSx} startIcon={<PictureAsPdfIcon sx={{ fontSize: 15 }} />}>
            {t('header.pdf')}
          </Button>
          <Button sx={exportBtnSx} startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}>
            {t('header.csv')}
          </Button>
          <Button
            onClick={onShare}
            startIcon={<ShareIcon sx={{ fontSize: 15 }} />}
            sx={{
              ...exportBtnSx,
              bgcolor: c.coral,
              color: c.white,
              border: 'none',
              '&:hover': { bgcolor: c.coralHover },
            }}
          >
            {t('header.share')}
          </Button>
        </Box>
      </Box>

      {/* Filter bar */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: c.ink48,
            fontWeight: 700,
            mr: 0.75,
          }}
        >
          {t('header.filters')}
        </Typography>
        <FilterPill label={t('header.filter.genderLabel')} value={t('header.filter.genderValue')} />
        <FilterPill
          label={t('header.filter.ageLabel')}
          value={t('header.filter.ageValue')}
          active={filters.age}
          onClick={() => setFilters((f) => ({ ...f, age: !f.age }))}
        />
        <FilterPill label={t('header.filter.cityLabel')} value={t('header.filter.cityValue')} />
        <FilterPill label={t('header.filter.deviceLabel')} value={t('header.filter.deviceValue')} />
        <FilterPill
          label={t('header.filter.durationLabel')}
          value={t('header.filter.durationValue')}
          active={filters.duration}
          onClick={() => setFilters((f) => ({ ...f, duration: !f.duration }))}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => setFilters({ age: false, duration: false })}
          sx={{ color: c.ink56, fontSize: 12.5, textTransform: 'none', fontWeight: 600, minWidth: 0 }}
        >
          {t('header.reset')}
        </Button>
      </Box>
    </Box>
  )
}
